import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FlagImport from 'react-world-flags'
import { Button } from '@/components/shadcn/button'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Input } from '@/components/shadcn/input'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { useAuthStore } from '@/store/auth.store'

// react-world-flags ships a UMD/CJS bundle. Under Vite the default import points
// to the module object ({ default: Flag }) instead of the component itself — so
// unwrap the nested default.
const Flag = ((FlagImport as unknown as { default?: typeof FlagImport }).default ??
  FlagImport) as typeof FlagImport

// Language code → country code for flag display, where the two differ
const FLAG_CODES: Record<string, string> = {
  en: 'gb', el: 'gr', cs: 'cz', uk: 'ua', he: 'il', zh: 'cn',
  fa: 'ir', sr: 'rs', ar: 'sa', da: 'dk', sv: 'se', et: 'ee', sl: 'si',
}

interface TranslationsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  /** Current translations (language → text), e.g. from useEntityTranslations.get() */
  value: Record<string, string>
  /** Called on „Übernehmen" with the edited state — persists nothing itself */
  onApply: (translations: Record<string, string>) => void
}

/**
 * Dumb, buffered translations dialog: one row per supportedLanguage
 * (flag + text field). Doesn't talk to any backend — „Übernehmen" only hands the
 * state back to the caller; saving happens when the parent object is saved.
 */
export function TranslationsDialog({ open, onOpenChange, title, value, onApply }: TranslationsDialogProps) {
  const { t, i18n } = useTranslation()
  const supportedLanguages = useAuthStore((s) => s.supportedLanguages)
  const [local, setLocal] = useState<Record<string, string>>(value)
  const [flagError, setFlagError] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (open) setLocal(value)
  }, [open, value])

  const displayLang = i18n.resolvedLanguage ?? i18n.language
  const languageName = (lang: string) => {
    try {
      return new Intl.DisplayNames([displayLang], { type: 'language' }).of(lang) ?? lang
    } catch {
      return lang
    }
  }

  const handleApply = () => {
    onApply(local)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title ?? t('translations.editTranslations')}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="flex flex-col gap-3 py-1">
            {supportedLanguages.map((lang) => (
              <div key={lang} className="flex items-center gap-3">
                {flagError[lang] ? (
                  <span className="w-8 shrink-0 text-center text-xs font-medium text-muted-foreground uppercase">
                    {lang}
                  </span>
                ) : (
                  <Flag
                    code={FLAG_CODES[lang.toLowerCase()] ?? lang}
                    style={{ width: 32, height: 20 }}
                    className="shrink-0 rounded-sm object-cover"
                    onError={() => setFlagError((prev) => ({ ...prev, [lang]: true }))}
                  />
                )}
                <Input
                  placeholder={languageName(lang)}
                  value={local[lang] ?? ''}
                  onChange={(e) => setLocal((prev) => ({ ...prev, [lang]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('buttons.cancel')}</Button>
          <Button type="button" onClick={handleApply}>{t('buttons.apply')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
